import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser, updateUserProfile } from "../api/users";
import { logoutUser } from "../api/auth";
import { getUserRatingStats, getUserRatings } from "../api/ratings";
import CustomAlert from "../components/CustomAlert";
import RatingDisplay from "../components/RatingDisplay";
import { BACKEND_BASE } from "../config";
import "./Profile.css";

export default function Profile() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("profile"); // profile, edit
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "info" });
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Rating state
  const [ratingStats, setRatingStats] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [ratingsLoading, setRatingsLoading] = useState(false);

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: "",
    phone: "",
    current_location: "",
    age: "",
    gender: "",
    bio: "",
    agency_name: "",
    location: "",
    description: "",
    services: "",
    website: "",
    logo: "",
  });

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    if (userData?.id) {
      loadRatings(userData.id);
    }
  }, [userData?.id]);

  async function loadUserData() {
    try {
      setLoading(true);
      const data = await getCurrentUser();
      setUserData(data);
      
      // Initialize edit form with current data
      const user = data.user || data;
      
      // Backend returns partner fields (age, gender, bio) at user level, not nested
      // Agency fields are still in agency_profile
      const agencyDetails = data.agency_profile;
      
      setEditFormData({
        name: user.name || "",
        phone: user.phone || "",
        current_location: user.current_location || "",
        // Partner fields are at user level
        age: user.age || "",
        gender: user.gender || "",
        bio: user.bio || "",
        // Agency fields are nested
        agency_name: agencyDetails?.agency_name || "",
        location: agencyDetails?.location || "",
        description: agencyDetails?.description || "",
        services: agencyDetails?.services || "",
        website: agencyDetails?.website || "",
        logo: agencyDetails?.logo || "",
      });
      
      setError(null);
    } catch (err) {
      console.error("Error loading user data:", err);
      setError("Failed to load profile. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function loadRatings(userId) {
    try {
      setRatingsLoading(true);
      const [stats, ratingsData] = await Promise.all([
        getUserRatingStats(userId),
        getUserRatings(userId, 0, 50)
      ]);
      setRatingStats(stats);
      setRatings(ratingsData);
    } catch (err) {
      console.error("Error loading ratings:", err);
    } finally {
      setRatingsLoading(false);
    }
  }

  useEffect(() => {
    if (userData?.user?.id) {
      loadRatings(userData.user.id);
    }
  }, [userData?.user?.id]);

  function handleLogout() {
    logoutUser();
    navigate("/login");
  }

  function handleEditProfile() {
    setIsEditing(true);
    setActiveTab("edit");
  }

  function handleCancelEdit() {
    setIsEditing(false);
    setActiveTab("profile");
    setSelectedPhoto(null);
    setPhotoPreview(null);
    loadUserData(); // Reset form data
  }

  function onEditFormChange(e) {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value,
    });
  }

  function handlePhotoChange(e) {
    const file = e.target.files[0];
    if (file) {
      const user = userData.user || userData;
      // Get file extension
      const fileExtension = file.name.split('.').pop();
      // Create new filename with user's name
      const userName = user.name.replace(/\s+/g, '_').toLowerCase();
      const newFileName = `${userName}_personal_photo.${fileExtension}`;
      
      // Create a new File object with the new name
      const renamedFile = new File([file], newFileName, { type: file.type });
      setSelectedPhoto(renamedFile);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setIsSaving(true);

    try {
      const user = userData.user || userData;
      const isPartner = user.role === "partner";

      // Use FormData for file upload
      const formData = new FormData();
      // Don't send name and phone - they cannot be changed
      formData.append('current_location', editFormData.current_location);

      // Add photo if selected
      if (selectedPhoto) {
        formData.append('personal_photo', selectedPhoto);
      }

      if (isPartner) {
        formData.append('age', editFormData.age);
        formData.append('gender', editFormData.gender);
        formData.append('bio', editFormData.bio);
      } else {
        formData.append('agency_name', editFormData.agency_name);
        formData.append('location', editFormData.location);
        formData.append('description', editFormData.description);
        formData.append('services', editFormData.services);
        if (editFormData.website) {
          formData.append('website', editFormData.website);
        }
        if (editFormData.logo) {
          formData.append('logo', editFormData.logo);
        }
      }

      await updateUserProfile(formData);
      await loadUserData();
      setIsEditing(false);
      setActiveTab("profile");
      setSelectedPhoto(null);
      setPhotoPreview(null);
      setAlert({
        show: true,
        message: "Profile updated successfully!",
        type: "success",
      });
    } catch (err) {
      console.error("Error updating profile:", err);
      setAlert({
        show: true,
        message: err.response?.data?.detail || "Failed to update profile. Please try again.",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading-spinner">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  // Handle nested user object structure
  const user = userData.user || userData;
  const isPartner = user.role === "partner";
  // Partner fields (age, gender, bio) are at user level
  // Agency fields are still in agency_profile
  const agencyDetails = userData.agency_profile;

  return (
    <div className="profile-container">
      {alert.show && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert({ ...alert, show: false })}
        />
      )}

      {/* Profile Header */}
      <div className="profile-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="profile-avatar-section">
            <div className="profile-avatar">
              {isPartner && user.personal_photo ? (
                <img src={`${BACKEND_BASE}${user.personal_photo}`} alt={user.name} />
              ) : !isPartner && agencyDetails?.logo ? (
                <img src={agencyDetails.logo.startsWith('http') ? agencyDetails.logo : `${BACKEND_BASE}${agencyDetails.logo}`} alt={user.name} />
              ) : (
                <div className="avatar-initials">
                  {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                </div>
              )}
            </div>
            <div className="profile-info">
              <h1 className="profile-name">{user.name}</h1>
              <p className="profile-role">
                {isPartner ? "🧳 Travel Partner" : "🏢 Travel Agency"}
              </p>
              
              {/* Display Rating */}
              {userData?.average_rating > 0 && (
                <div className="profile-rating-badge">
                  <span className="rating-stars">
                    {'⭐'.repeat(Math.round(userData.average_rating))}
                  </span>
                  <span className="rating-value">
                    {userData.average_rating.toFixed(1)}
                  </span>
                  <span className="rating-count">
                    ({userData.total_ratings} {userData.total_ratings === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
              )}
              
              <p className="profile-email">{user.email}</p>
              {isPartner && user.current_location && (
                <p className="profile-location">📍 {user.current_location}</p>
              )}
              {!isPartner && agencyDetails?.location && (
                <p className="profile-location">📍 {agencyDetails.location}</p>
              )}
            </div>
          </div>
          <div className="profile-actions">
            <button className="edit-profile-btn" onClick={handleEditProfile}>
              ✏️ Edit Profile
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="profile-content">
        <div className="container">
          <div className="profile-tabs">
            <button
              className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              📋 Profile Details
            </button>
            {isEditing && (
              <button
                className={`tab ${activeTab === 'edit' ? 'active' : ''}`}
                onClick={() => setActiveTab('edit')}
              >
                ✏️ Edit Profile
              </button>
            )}
          </div>

          {/* Profile Details */}
          {activeTab === 'profile' && (
            <div className="profile-details">
              <div className="details-section">
                <h2 className="section-title">Contact Information</h2>
                <div className="details-grid">
                  <div className="detail-item">
                    <div className="detail-icon">📧</div>
                    <div className="detail-info">
                      <label>Email Address</label>
                      <span className="detail-value">{user.email}</span>
                    </div>
                  </div>
                  
                  {user.phone && (
                    <div className="detail-item">
                      <div className="detail-icon">📱</div>
                      <div className="detail-info">
                        <label>Phone Number</label>
                        <span className="detail-value">{user.phone}</span>
                      </div>
                    </div>
                  )}
                  
                  {user.current_location && (
                    <div className="detail-item">
                      <div className="detail-icon">📍</div>
                      <div className="detail-info">
                        <label>Current Location</label>
                        <span className="detail-value">{user.current_location}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {isPartner ? (
                // Partner Details
                <div className="details-section">
                  <h2 className="section-title">Personal Information</h2>
                  <div className="details-grid">
                    <div className="detail-item">
                      <div className="detail-icon">👤</div>
                      <div className="detail-info">
                        <label>Age</label>
                        <span className="detail-value">{user.age} years</span>
                      </div>
                    </div>

                    <div className="detail-item">
                      <div className="detail-icon">⚧️</div>
                      <div className="detail-info">
                        <label>Gender</label>
                        <span className="detail-value">{user.gender}</span>
                      </div>
                    </div>
                  </div>

                  {user.bio && (
                    <div className="bio-section">
                      <label>Bio</label>
                      <p className="bio-text">{user.bio}</p>
                    </div>
                  )}
                  
                  <div className="trips-cta">
                    <p className="cta-text">✈️ Manage your trips and find travel buddies in the "My Trips" section</p>
                  </div>
                </div>
              ) : (
                // Agency Details
                <div className="details-section">
                  <h2 className="section-title">Agency Information</h2>
                  
                  {agencyDetails?.logo && (
                    <div className="agency-logo-display">
                      <label>Agency Logo</label>
                      <img 
                        src={agencyDetails.logo.startsWith('http') ? agencyDetails.logo : `${BACKEND_BASE}${agencyDetails.logo}`} 
                        alt="Agency Logo" 
                        className="agency-logo-image"
                      />
                    </div>
                  )}
                  
                  <div className="details-grid">
                    <div className="detail-item">
                      <div className="detail-icon">🏢</div>
                      <div className="detail-info">
                        <label>Agency Name</label>
                        <span className="detail-value">{agencyDetails?.agency_name}</span>
                      </div>
                    </div>

                    <div className="detail-item">
                      <div className="detail-icon">📍</div>
                      <div className="detail-info">
                        <label>Location</label>
                        <span className="detail-value">{agencyDetails?.location}</span>
                      </div>
                    </div>

                    {agencyDetails?.website && (
                      <div className="detail-item">
                        <div className="detail-icon">🌐</div>
                        <div className="detail-info">
                          <label>Website</label>
                          <a href={agencyDetails.website} target="_blank" rel="noopener noreferrer" className="detail-value link">
                            {agencyDetails.website}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  {agencyDetails?.description && (
                    <div className="bio-section">
                      <label>Description</label>
                      <p className="bio-text">{agencyDetails.description}</p>
                    </div>
                  )}

                  {agencyDetails?.services && (
                    <div className="bio-section">
                      <label>Services</label>
                      <p className="bio-text">{agencyDetails.services}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="details-section">
                <h2 className="section-title">Account Information</h2>
                <div className="details-grid">
                  <div className="detail-item">
                    <div className="detail-icon">📋</div>
                    <div className="detail-info">
                      <label>Account Type</label>
                      <span className="detail-value">{user.role}</span>
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-icon">✅</div>
                    <div className="detail-info">
                      <label>Account Status</label>
                      <span className="detail-value">
                        {user.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-icon">📆</div>
                    <div className="detail-info">
                      <label>Member Since</label>
                      <span className="detail-value">
                        {new Date(user.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ratings Section */}
              <div className="details-section ratings-section">
                <h2 className="section-title">⭐ Your Ratings & Reviews</h2>
                <RatingDisplay 
                  ratings={ratings} 
                  stats={ratingStats} 
                  loading={ratingsLoading} 
                />
              </div>
            </div>
          )}

          {/* Edit Profile Form */}
          {activeTab === 'edit' && isEditing && (
            <form onSubmit={handleSaveProfile} className="edit-profile-form">
              {/* Profile Photo Upload - Partner Only */}
              {isPartner && (
              <div className="details-section">
                <h2 className="section-title">Profile Photo</h2>
                <div className="photo-upload-section">
                  <div className="photo-preview">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" className="preview-image" />
                    ) : user.personal_photo ? (
                      <img src={`${BACKEND_BASE}${user.personal_photo}`} alt={user.name} className="preview-image" />
                    ) : (
                      <div className="avatar-initials-large">
                        {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                      </div>
                    )}
                    <label htmlFor="photo-upload" className="photo-upload-btn" title="Upload new photo">
                      <svg className="camera-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 15.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z"/>
                        <path fillRule="evenodd" d="M4 5.25A2.25 2.25 0 016.25 3h1.5a.75.75 0 00.75-.75v-.5a.75.75 0 01.75-.75h5.5a.75.75 0 01.75.75v.5a.75.75 0 00.75.75h1.5A2.25 2.25 0 0120 5.25v12.5A2.25 2.25 0 0117.75 20H6.25A2.25 2.25 0 014 17.75V5.25zm8-1.25a5.5 5.5 0 100 11 5.5 5.5 0 000-11z" clipRule="evenodd"/>
                      </svg>
                      <input
                        id="photo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                  <p className="photo-hint">Click the camera icon to upload a new photo</p>
                </div>
              </div>
              )}

              <div className="details-section">
                <h2 className="section-title">Edit Contact Information</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={editFormData.name}
                      onChange={onEditFormChange}
                      disabled
                      className="form-input disabled"
                      title="Name cannot be changed"
                    />
                    <small className="field-note">Name cannot be changed</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={editFormData.phone}
                      onChange={onEditFormChange}
                      disabled
                      className="form-input disabled"
                      title="Phone number cannot be changed"
                    />
                    <small className="field-note">Phone number cannot be changed</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="current_location">Current Location</label>
                    <input
                      id="current_location"
                      name="current_location"
                      type="text"
                      value={editFormData.current_location}
                      onChange={onEditFormChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              {isPartner ? (
                <div className="details-section">
                  <h2 className="section-title">Edit Personal Information</h2>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="age">Age</label>
                      <input
                        id="age"
                        name="age"
                        type="number"
                        value={editFormData.age}
                        onChange={onEditFormChange}
                        required
                        min="18"
                        max="100"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="gender">Gender</label>
                      <select
                        id="gender"
                        name="gender"
                        value={editFormData.gender}
                        onChange={onEditFormChange}
                        required
                        className="form-input"
                      >
                        <option value="">Select gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="bio">Bio</label>
                    <textarea
                      id="bio"
                      name="bio"
                      value={editFormData.bio}
                      onChange={onEditFormChange}
                      rows="4"
                      className="form-input"
                      placeholder="Tell us about yourself and your travel preferences..."
                    />
                  </div>
                </div>
              ) : (
                <div className="details-section">
                  <h2 className="section-title">Edit Agency Information</h2>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="agency_name">Agency Name</label>
                      <input
                        id="agency_name"
                        name="agency_name"
                        type="text"
                        value={editFormData.agency_name}
                        onChange={onEditFormChange}
                        required
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="location">Location</label>
                      <input
                        id="location"
                        name="location"
                        type="text"
                        value={editFormData.location}
                        onChange={onEditFormChange}
                        required
                        className="form-input"
                      />
                    </div>                  <div className="form-group">
                    <label htmlFor="website">Website</label>
                    <input
                      id="website"
                      name="website"
                      type="url"
                      value={editFormData.website}
                      onChange={onEditFormChange}
                      className="form-input"
                      placeholder="https://yourwebsite.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="logo">Logo URL</label>
                    <input
                      id="logo"
                      name="logo"
                      type="url"
                      value={editFormData.logo}
                      onChange={onEditFormChange}
                      className="form-input"
                      placeholder="https://yourlogo.com/logo.png"
                    />
                  </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={editFormData.description}
                      onChange={onEditFormChange}
                      rows="3"
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="services">Services</label>
                    <textarea
                      id="services"
                      name="services"
                      value={editFormData.services}
                      onChange={onEditFormChange}
                      rows="2"
                      className="form-input"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="btn-cancel"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
